import { Alert, App as AntApp, Button, Form, Input, Modal, Space, Table, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { notificationKeys, templateTextProblems, useMessageTemplates, type MessageTemplate } from "../app/notifications.ts";
import { formatSchoolDateTime } from "../app/time.ts";

/** Mirrors MessageTemplate.TextMaxLength on the server. */
const textMaxLength = 3000;

/** Telegram message templates (story 118). Requires notifications.manage; administrators edit only the text. */
export function MessageTemplatesPage() {
  const { t } = useTranslation();
  const templates = useMessageTemplates();
  const [editing, setEditing] = useState<MessageTemplate | undefined>(undefined);

  const columns = [
    {
      key: "key",
      title: t("notifications.templates.columns.key"),
      width: 200,
      render: (_: unknown, template: MessageTemplate) => <Typography.Text code>{template.key}</Typography.Text>,
    },
    { key: "description", title: t("notifications.templates.columns.description"), dataIndex: "description", width: 240 },
    {
      key: "text",
      title: t("notifications.templates.columns.text"),
      render: (_: unknown, template: MessageTemplate) => <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{template.text}</Typography.Paragraph>,
    },
    {
      key: "updatedAt",
      title: t("notifications.templates.columns.updatedAt"),
      width: 150,
      render: (_: unknown, template: MessageTemplate) => formatSchoolDateTime(template.updatedAt),
    },
    {
      key: "actions",
      title: t("notifications.templates.columns.actions"),
      width: 130,
      render: (_: unknown, template: MessageTemplate) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(template)} aria-label={t("notifications.templates.editNamed", { key: template.key })}>
          {t("notifications.templates.edit")}
        </Button>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("notifications.templates.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("notifications.templates.subtitle")}</Typography.Text>
      </div>
      {templates.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void templates.refetch()}>{t("app.retry")}</Button>} />}
      <Table<MessageTemplate>
        rowKey="id"
        size="middle"
        loading={templates.isPending}
        columns={columns}
        dataSource={templates.data ?? []}
        pagination={false}
        scroll={{ x: 900 }}
        locale={{ emptyText: t("notifications.templates.empty") }}
      />
      <TemplateModal template={editing} onClose={() => setEditing(undefined)} />
    </Space>
  );
}

interface TemplateForm {
  text?: string;
}

function TemplateModal({ template, onClose }: { template: MessageTemplate | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<TemplateForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const parameters = template?.parameters ?? [];

  useEffect(() => {
    if (template) {
      form.resetFields();
      form.setFieldsValue({ text: template.text });
      setSummary([]);
    }
  }, [template, form]);

  const save = useMutation({
    mutationFn: async (values: TemplateForm) => {
      if (!template) throw new Error(t("app.error"));
      const result = await api.PUT("/notifications/templates/{id}", { params: { path: { id: template.id } }, body: { text: values.text ?? "" } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: notificationKeys.templates }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t("notifications.templates.saved"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  function insert(name: string) {
    const current = form.getFieldValue("text") as string | undefined;
    form.setFieldsValue({ text: `${current ?? ""}{${name}}` });
    void form.validateFields(["text"]);
  }

  async function validateText(_: unknown, value: string | undefined) {
    const problems = templateTextProblems(value ?? "", parameters);
    if (problems.unknown.length > 0) throw new Error(t("notifications.templates.unknownPlaceholder", { names: problems.unknown.join(", ") }));
    if (problems.strayBrace) throw new Error(t("notifications.templates.strayBrace"));
  }

  return (
    <Modal
      open={template !== undefined}
      title={t("notifications.templates.editTitle")}
      okText={t("family.save")}
      cancelText={t("family.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
      width={640}
    >
      {template && (
        <Space orientation="vertical" size="small" style={{ width: "100%", marginBottom: 16 }}>
          <Typography.Text code>{template.key}</Typography.Text>
          <Typography.Text type="secondary">{template.description}</Typography.Text>
        </Space>
      )}
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form<TemplateForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        <Form.Item
          name="text"
          label={t("notifications.templates.text")}
          rules={[{ required: true, whitespace: true, message: t("family.validation.required") }, { validator: validateText }]}
        >
          <Input.TextArea autoSize={{ minRows: 4, maxRows: 12 }} maxLength={textMaxLength} showCount autoFocus />
        </Form.Item>
      </Form>
      {parameters.length > 0 ? (
        <Space wrap size={[8, 8]}>
          <Typography.Text type="secondary">{t("notifications.templates.allowed")}</Typography.Text>
          {parameters.map((name) => (
            <Button key={name} size="small" onClick={() => insert(name)} aria-label={t("notifications.templates.insert", { placeholder: `{${name}}` })}>
              {`{${name}}`}
            </Button>
          ))}
        </Space>
      ) : (
        <Typography.Text type="secondary">{t("notifications.templates.noParameters")}</Typography.Text>
      )}
    </Modal>
  );
}
